import { useState } from 'react'

import { ProgressBar, Text } from '@factorialco/deprecated-design-system'

import { UploadStatus, useDirectUpload } from 'modules/core/hooks/useDirectUpload'
import { i18nDateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { FileTypeCategory, UploadValue } from 'modules/trainings/components/Files/types'
import { formatFileSize } from 'modules/trainings/components/Files/utils'

import Dropper from './Dropper'
import { UploadResult } from './UploadResult'

export default function DirectUploader({
  fileTypes,
  onUpload,
  resourceType,
  resourceId,
  description,
  initialFile,
  onDelete,
}: {
  resourceType: string
  resourceId?: string
  fileTypes: FileTypeCategory[]
  onUpload(upload: UploadValue): void
  description: string
  initialFile?: File
  onDelete?(): void
}) {
  const [file, setFile] = useState<File | undefined>(initialFile)
  const [error, setError] = useState<string>()

  const {
    upload: directUploadFile,
    status: uploadStatus,
    progress,
    cancelUpload,
  } = useDirectUpload({
    resourceType,
    resourceId,
  })

  function handleError(errorMessage: string) {
    setFile(undefined)
    setError(errorMessage)
  }

  if (!file) {
    return (
      <>
        <Dropper
          error={Boolean(error)}
          fileTypes={fileTypes}
          onDrop={async (result) => {
            if (result.type === 'error') {
              handleError(result.error)
              return
            }
            try {
              setFile(result.file)
              const uploadResult = await directUploadFile(result.file)
              switch (uploadResult.type) {
                case 'aborted':
                  handleError(i18n.t('trainings.trainings_files_modal.failed_to_upload'))
                  break
                case 'success':
                  onUpload({ file: result.file, signedId: uploadResult.signedId })
                  break
              }
            } catch (uploadError: unknown) {
              handleError(i18n.t('trainings.trainings_files_modal.failed_to_upload'))
            }
          }}
          description={description}
        />
        {error && (
          <Text color="radicalRed1000" size="200">
            {error}
          </Text>
        )}
      </>
    )
  }

  return (
    <UploadResult
      onDelete={() => {
        if (uploadStatus === 'uploading') {
          cancelUpload()
        }
        setFile(undefined)
        onDelete?.()
      }}
      file={file}
    >
      <ResultMiddle status={uploadStatus} progress={progress} file={file} />
    </UploadResult>
  )
}

function ResultMiddle({
  status,
  progress,
  file,
}: {
  file: File
  status: UploadStatus
  progress: number
}) {
  switch (status) {
    case 'processing': {
      return <Text size="200">{i18n.t('trainings.trainings_files_modal.processing')}</Text>
    }
    case 'uploading': {
      if (progress < 1)
        return (
          <ProgressBar
            barColor="purple200"
            progressColor="purple1000"
            percentage={Math.max(Math.round(progress * 100), 1)}
            amount={progress}
            total={1}
          />
        )
    }
    case 'success': {
      return (
        <Text size="200">
          {`${i18nDateToString(new Date(file.lastModified), 'ui_short_with_time')} ${formatFileSize(
            file.size
          )}`}
        </Text>
      )
    }
    default: {
      return null
    }
  }
}
